import {useConfig} from '@aboutyou/config';
import {SpotlightSection_ContentElement} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {useHistory} from '@aboutyou/router';
import {
  Chip,
  SlideAmbientProps,
  SponsoredWithBlurIcon,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, MouseEvent, useEffect, useMemo} from 'react';
import {useSponsoredProductPopup} from '../../../backend-services/SponsoredProductPopup/utils/useSponsoredProductPopup';
import {useSplitFirstProductIds} from '../../hooks/useSplitFirstProductIds';
import {StyledBrandLogo} from './Shared';
import {SlidePropsType} from './types';

export const ContentSlide: FC<
  {
    content: SpotlightSection_ContentElement;
  } & SlidePropsType & {SlideUIComponent: FC<SlideAmbientProps>}
> = ({
  content,
  isActive,
  isCloseToViewport,
  onSlideClick,
  'data-testid': dataTestid,
  SlideUIComponent,
  uniqueKey = '',
  ...rest
}) => {
  const {device} = useConfig();
  const isMobile = device === 'mobile';
  const history = useHistory();

  const {linkWithoutFirstProductIds, fullLink} = useSplitFirstProductIds(
    content.linkTarget.url,
  );

  const onBannerTargetClick = () => {
    if (fullLink) {
      history.push(fullLink);
    }
  };

  const productImages = useMemo(
    () =>
      content.productGallery.map(product => ({
        url: product.image.url,
        description: product.image.description,
      })),
    [content.productGallery],
  );

  return (
    <SlideUIComponent
      {...rest}
      uniqueKey={uniqueKey}
      data-testid={dataTestid}
      slideImage={
        isMobile ? content.coverSquareImage : content.coverLandscapeImage
      }
      slideVideo={
        isMobile ? content.coverPortraitVideo : content.coverLandscapeVideo
      }
      bannerTarget={{
        url: linkWithoutFirstProductIds || content.linkTarget.url,
        external: content.linkTarget.external,
        onClick: onBannerTargetClick,
      }}
      subline={content.topline}
      headline={content.headline}
      productImages={productImages}
      brandLogo={
        <>
          {content.logoTransparentImage && (
            <StyledBrandLogo
              name={content.logoTransparentImage.description}
              image={content.logoTransparentImage}
              maxHeight="50px"
              maxWidth="30%"
            />
          )}
        </>
      }
      isActive={Boolean(isActive)}
      isCloseToViewport={Boolean(isCloseToViewport)}
      onSlideClick={onSlideClick}
      floatingChip={<StyledChip label={content.recommendationReason} />}
      floatingTopLeft={
        content.sponsored ? (
          <SponsorContentButton
            label={content.sponsored.label}
            promotionId={content.promotionTracker.promotionId}
            onPauseAutomaticSlide={rest.onPauseAutomaticSlide}
            onResumeAutomaticSlide={rest.onResumeAutomaticSlide}
          />
        ) : undefined
      }
    />
  );
};

const SponsorContentButton: FC<{
  label?: string;
  promotionId: string;
  onPauseAutomaticSlide?: VoidFunction;
  onResumeAutomaticSlide?: VoidFunction;
}> = ({label, promotionId, onPauseAutomaticSlide, onResumeAutomaticSlide}) => {
  const {onSponsoredClick, isOpen} = useSponsoredProductPopup();

  const onClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSponsoredClick({e, promotionId});
  };

  useEffect(() => {
    if (isOpen) {
      onPauseAutomaticSlide?.();
    } else {
      onResumeAutomaticSlide?.();
    }
  }, [isOpen]);

  return (
    <StyledSponsoredWithBlurIcon
      label={label}
      onClick={onClick}
      color="Fill.White"
      iconPosition="left"
    />
  );
};

const StyledChip = styled(Chip)`
  margin: auto;
  text-align: center;
  max-width: 100%;

  > span {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  ${tabletAndUpLinaria(`
    margin: unset;
    line-height: 1.6;
  `)}
`;

const StyledSponsoredWithBlurIcon = styled(SponsoredWithBlurIcon)`
  position: relative;
  inset: 12px;

  && {
    justify-content: start;
  }

  ${tabletAndUpLinaria(`
    inset: 16px;
  `)}
`;
